import React, { useEffect, useState, useMemo } from "react";
import { useForm, Controller, useFieldArray, useWatch } from "react-hook-form";
import DatePicker from "react-datepicker";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { productData, getAllQuantityTypes, deleteQuantityType } from "../common/globalapi/Api";
import { toast } from "react-toastify";
import Select, { components } from "react-select";
import moment from "moment";
import CreateQuantityType from "../product/CreateQuantityType";

// Custom Option to render Edit and Delete buttons inline
const CustomOption = (props) => {
    return (
        <components.Option {...props}>
            <div className="d-flex justify-content-between align-items-center w-100">
                <span className="text-truncate">{props.data.label}</span>
                {props.data.id && (
                    <div className="d-flex gap-2">
                        <button
                            type="button"
                            className="btn btn-sm btn-link p-0 text-primary"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (props.selectProps.onEditItem) props.selectProps.onEditItem(props.data);
                            }}
                            title="Edit"
                        >
                            <i className="bi bi-pencil"></i>
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-link p-0 text-danger"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (props.selectProps.onDeleteItem) props.selectProps.onDeleteItem(props.data);
                            }}
                            title="Delete"
                        >
                            <i className="bi bi-trash"></i>
                        </button>
                    </div>
                )}
            </div>
        </components.Option>
    );
};


const NewPurchase = () => {
    const navigate = useNavigate();
    const { state } = useLocation();
    console.log("state", state)
    const {
        control,
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
    } = useForm({
        defaultValues: {
            vendorDetails: [
                {
                    product: "",
                    quantity: "",
                    quantityType: "",
                    otherQuantityType: "",
                    productFor: "",
                    unitPrice: "",
                    price: "",
                    salesDate: moment().format("YYYY-MM-DD"),
                    hsncode: "",
                    gstPercentage: "",
                    manufacturingDate: "",
                    expiryDate: "",
                    lowLevelCustomer: "",
                    midLevelCustomer: "",
                    highLevelCustomer: "",
                    others: "",
                    stock: "In Stock",
                    otherAmount: "0.00",
                    gstAmount: "0.00",
                    totalAmount: "0.00",
                },
            ],
            purchaseDate: moment().format("YYYY-MM-DD"),
            transactionMode: "Online",
            transactionStatus: "Pending",
        },
    });

    const { fields, append, remove, replace } = useFieldArray({
        control,
        name: "vendorDetails",
    });

    const vendorDetails = useWatch({ name: "vendorDetails", control });

    // CALCULATION LOGIC
    useEffect(() => {
        (vendorDetails || []).forEach((item, index) => {
            const price = parseFloat(item?.unitPrice) || 0;
            const qty = parseFloat(item?.quantity) || 0;
            const gstPercent = parseFloat(item?.gstPercentage) || 0;
            const other = parseFloat(item?.otherAmount) || 0;

            const subtotal = price * qty;
            const gstAmount = (subtotal * gstPercent) / 100;
            const total = subtotal + gstAmount + other;

            // Only update if changed, to avoid infinite loop
            if (item?.gstAmount !== gstAmount.toFixed(2)) {
                setValue(`vendorDetails.${index}.gstAmount`, gstAmount.toFixed(2));
            }
            if (item?.totalAmount !== total.toFixed(2)) {
                setValue(`vendorDetails.${index}.totalAmount`, total.toFixed(2));
            }
        });
    }, [vendorDetails, setValue]);

    const userId = sessionStorage.getItem("userId");
    const queryClient = useQueryClient();
    const [editingQuantityType, setEditingQuantityType] = useState(null);

    // Fetch Quantity Types
    const {
        data: quantityTypesData = [],
        isLoading: quantityTypesLoading,
    } = useQuery({
        queryKey: ["quantityTypes", userId],
        queryFn: async () => {
            if (!userId) return [];
            const resp = await getAllQuantityTypes(userId);
            return resp.data || [];
        },
        enabled: !!userId,
        refetchOnWindowFocus: false,
    });

    // DELETE API for Quantity Type
    const deleteQuantityMutation = useMutation({
        mutationFn: async (id) => {
            const response = await deleteQuantityType(userId, id);
            return response.data;
        },
        onSuccess: () => {
            toast.success("Quantity Type deleted successfully!");
            queryClient.invalidateQueries(["quantityTypes", userId]);
        },
        onError: (error) => {
            toast.error(error?.response?.data || "Failed to delete Quantity Type.");
        }
    });

    const handleDeleteQuantityType = (data) => {
        if (window.confirm(`Are you sure you want to delete '${data.label}'?`)) {
            deleteQuantityMutation.mutate(data.id);
        }
    };

    const handleEditQuantityType = (data) => {
        setEditingQuantityType(data.originalData);
        const modalBtn = document.getElementById("hiddenCreateQtyModalBtn");
        if (modalBtn) modalBtn.click();
    };
    const companyDetails = (() => {
        try {
            return JSON.parse(localStorage.getItem("companyDetails") || "{}");
        } catch (e) {
            return {};
        }
    })();
    // POST API START
    const { mutate, isPending: postLoading } = useMutation({
        mutationFn: async (postData) => {
            const response = await axios.post(
                `${process.env.REACT_APP_HOST}/api/Purchases/addpurchase`,
                postData
            );
            return response.data;
        },
        onSuccess: () => {
            toast.success("Purchase Added Successfully!");
            setTimeout(() => navigate("/viewPurchase"), 2000);
        },
        onError: (error) => {
            toast.error("Failed to add purchase. Please try again.");
        },
    });

    // UPDATE API
    const { mutate: updateApi, isPending: putLoading } = useMutation({
        mutationFn: async (postData) => {
            const response = await axios.put(
                `${process.env.REACT_APP_HOST}/api/Purchases/${userId}/${state?.purchaseId || state?.id}`,
                postData
            );
            return response.data;
        },
        onSuccess: () => {
            toast.success("Purchase Updated Successfully!");
            setTimeout(() => navigate("/viewPurchase"), 2000);
        },
        onError: (error) => {
            toast.error("Failed to update the purchase. Please try again.");
        },
    });

    // SETVALUE FOR STATE VALUE
    console.log("incoming state", state);

    useEffect(() => {
        if (!state) {
            // New purchase: populate registerId
            const reg = companyDetails?.id || "";
            if (reg) setValue("registerId", reg);
            return;
        }

        // Normalize top-level fields (state may be a purchase object, an array of items, or a single item)
        const first = Array.isArray(state) ? state[0] : state;

        setValue(
            "purchaseDate",
            first?.purchaseDate ? moment(first?.purchaseDate).format("YYYY-MM-DD") : ""
        );
        setValue("transactionMode", first?.transactionMode || first?.transactionMode || "");
        setValue("transactionStatus", first?.transactionStatus || first?.status || "");
        setValue("invoiceNumber", first?.invoiceNo || first?.invoiceNumber || "");
        // setValue("otherAmount", first?.otherAmount || first?.otherAmount || "");

        // incomingItems can be nested arrays or flat item(s)
        let incomingItemsRaw = state?.vendorDetails || state?.items || state;

        let incomingItems = [];
        if (Array.isArray(incomingItemsRaw)) {
            incomingItems = incomingItemsRaw;
        } else if (incomingItemsRaw && typeof incomingItemsRaw === "object") {
            // treat as single item
            incomingItems = [incomingItemsRaw];
        }

        if (incomingItems.length > 0) {
            replace(
                incomingItems.map((item) => ({
                    product: item?.product || item?.productName || item?.productNameEnglish || "",
                    quantity: item?.quantity ?? "",
                    unitPrice: item?.unitPrice ?? item?.purchasePrice ?? "",
                    quantityType: item?.quantityType ?? "",
                    productFor: item?.productFor ?? "",
                    salesDate: item?.salesDate ?? item?.purchaseDate ?? "",
                    hsncode: item?.hsnCode || item?.hsncode || "",
                    gstPercentage: item?.gst ?? item?.gstPercentage ?? "",
                    manufacturingDate: item?.manufacturingDate ? moment(item.manufacturingDate).format("YYYY-MM-DD") : "",
                    expiryDate: item?.expiryDate ? moment(item.expiryDate).format("YYYY-MM-DD") : "",
                    lowLevelCustomer: item?.lowLevelCustomerPrice ?? item?.lowLevelCustomer ?? "",
                    midLevelCustomer: item?.midLevelCustomerPrice ?? item?.midLevelCustomer ?? "",
                    highLevelCustomer: item?.highLevelCustomerPrice ?? item?.highLevelCustomer ?? "",
                    others: item?.others ?? "",
                    stock: item?.stockStatus ?? item?.stock ?? "",
                    otherAmount: item?.otherAmount ?? "",
                    gstAmount: item?.gstAmount ?? "",
                    totalAmount: item?.totalAmount ?? "",
                }))
            );
        } else {
            // no items found: still ensure registerId
            const reg = companyDetails?.id || "";
            if (reg) setValue("registerId", reg);
        }
    }, [state, setValue, replace]);

    // GET API START FOR PRODUCT
    const {
        data: productDetails,
        isLoading: productLoading,
        refetch: productRefetch,
    } = useQuery({
        queryKey: ["product", userId],
        queryFn: () => productData(userId),
        enabled: !!userId,
        refetchOnWindowFocus: false,
    });

    // GET API END FOR PRODUCT

    let products = productDetails?.data;

    const productOptions = products?.map((product) => ({
        label: product?.productName || product?.productNameEnglish || "",
        value: product?.productName || product?.productNameEnglish || "",
    }));

    // Custom MenuList to append an Add Product button at the end of dropdown
    const CustomMenuList = (menuProps) => (
        <components.MenuList {...menuProps}>
            {menuProps.children}
            <div style={{ padding: 8, borderTop: '1px solid #eee' }}>
                <button
                    type="button"
                    className="btn btn-sm btn-outline-primary w-100"
                    onClick={() => {
                        navigate('/addProduct');
                        if (menuProps?.selectProps?.onMenuClose) menuProps.selectProps.onMenuClose();
                    }}
                >
                    + Add Product
                </button>
            </div>
        </components.MenuList>
    );

    // SETVALUE FOR QUANITYTYPE AND UNITPRICE

    // useEffect(() => {
    //   if (!vendorDetails || !Array.isArray(vendorDetails)) return;

    //   vendorDetails?.forEach((item, index) => {
    //     const selectedProduct = products?.find(
    //       (product) => product?.productNameEnglish === item?.product
    //     );

    //     console.log("selected product", selectedProduct)

    //     if (selectedProduct) {
    //       if (item?.quantityType !== selectedProduct?.quantityType) {
    //         setValue(
    //           `vendorDetails.${index}.quantityType`,
    //           selectedProduct?.quantityType || ""
    //         );
    //       }
    //     } else {
    //       if (item?.quantityType) {
    //         setValue(`vendorDetails.${index}.quantityType`, "");
    //       }
    //     }
    //   });
    // }, [vendorDetails, products, setValue]);

    // FORM SUBMBIT
    const onSubmit = (data) => {
        // Build payload matching required JSON shape
        const purchaseDate = data?.purchaseDate ? moment(data.purchaseDate).format("YYYY-MM-DD") : "";
        const items = (vendorDetails || []).map((it, index) => ({
            productName: it?.product || "",
            hsnCode: it?.hsncode || it?.hsnCode || "",
            purchasePrice: it?.unitPrice ? Number(it.unitPrice) : it?.unitPrice,
            quantity: it?.quantity ? Number(it.quantity) : it?.quantity,
            quantityType: it?.quantityType === "Other" ? it?.otherQuantityType : it?.quantityType || "",
            productFor: it?.productFor || "",
            gst: it?.gstPercentage ? Number(it.gstPercentage) : it?.gst || null,
            manufacturingDate: it?.manufacturingDate ? moment(it.manufacturingDate).format("YYYY-MM-DD") : "",
            expiryDate: it?.expiryDate ? moment(it.expiryDate).format("YYYY-MM-DD") : "",
            stockStatus: it?.stock || it?.stockStatus || "",
            highLevelCustomerPrice: it?.highLevelCustomer ? Number(it.highLevelCustomer) : it?.highLevelCustomer || null,
            lowLevelCustomerPrice: it?.lowLevelCustomer ? Number(it.lowLevelCustomer) : it?.lowLevelCustomer || null,
            midLevelCustomerPrice: it?.midLevelCustomer ? Number(it.midLevelCustomer) : it?.midLevelCustomer || null,
            otherAmount: it?.otherAmount ? Number(it.otherAmount) : 0,
            gstAmount: it?.gstAmount ? Number(it.gstAmount) : 0,
            totalAmount: it?.totalAmount ? Number(it.totalAmount) : 0,
        }));

        const payload = {
            registerId: companyDetails?.id || data?.registerId || "",
            purchaseDate: purchaseDate,
            invoiceNo: data?.invoiceNumber || data?.invoiceNo || "",
            transactionMode: data?.transactionMode || "",
            status: data?.transactionStatus || data?.status || "",
            items: items,
        };

        if (state?.id) {
            updateApi(payload);
        } else {
            mutate(payload);
        }
    };

    return (
        <>
            <div className="form_main_block">
                <div className="form_sub_block">
                    <form className="p-4" onSubmit={handleSubmit(onSubmit)}>
                        {fields?.map((field, index) => {
                            return (
                                <div key={field?.id}>
                                    <div className="row px-3 d-flex gap-3 my-2 append_order">
                                        <div className="col-12 col-md-2 d-flex flex-column gap-2 mb-0 p-0">
                                            <label className="executive_label">
                                                Product Name&nbsp;
                                                <span style={{ color: "red" }}>*</span>
                                            </label>

                                            <Controller
                                                name={`vendorDetails.${index}.product`}
                                                control={control}
                                                rules={{ required: "Product is required" }}
                                                render={({ field, fieldState: { error } }) => (
                                                    <div style={{ marginBottom: "1rem" }}>
                                                        <Select
                                                            {...field}
                                                            options={productOptions}
                                                            placeholder="Select Product"
                                                            isClearable
                                                            components={{ MenuList: CustomMenuList }}
                                                            value={
                                                                productOptions?.find(
                                                                    (o) => o.value === field.value
                                                                ) || null
                                                            }
                                                            onChange={(selectedOption) => {
                                                                const selectedValue =
                                                                    selectedOption?.value || "";
                                                                field.onChange(selectedValue); // RHF: update product field

                                                                // Try to locate full product record
                                                                const matchedProduct = products?.find(
                                                                    (p) =>
                                                                        (p.productName === selectedValue) || (p.productNameEnglish === selectedValue)
                                                                );

                                                                if (matchedProduct) {
                                                                    setValue(
                                                                        `vendorDetails.${index}.hsncode`,
                                                                        matchedProduct?.hsnCode ?? ""
                                                                    );
                                                                    setValue(
                                                                        `vendorDetails.${index}.unitPrice`,
                                                                        matchedProduct?.purchasePrice ?? ""
                                                                    );
                                                                    setValue(
                                                                        `vendorDetails.${index}.quantityType`,
                                                                        matchedProduct?.quantityType ?? ""
                                                                    );
                                                                    setValue(
                                                                        `vendorDetails.${index}.highLevelCustomer`,
                                                                        matchedProduct?.highLevelCustomerPrice ?? ""
                                                                    );
                                                                    setValue(
                                                                        `vendorDetails.${index}.midLevelCustomer`,
                                                                        matchedProduct?.midLevelCustomerPrice ?? ""
                                                                    );
                                                                    setValue(
                                                                        `vendorDetails.${index}.lowLevelCustomer`,
                                                                        matchedProduct?.lowLevelCustomerPrice ?? ""
                                                                    );
                                                                    setValue(
                                                                        `vendorDetails.${index}.stock`,
                                                                        matchedProduct?.stockStatus || "In Stock"
                                                                    );
                                                                    setValue(
                                                                        `vendorDetails.${index}.manufacturingDate`,
                                                                        matchedProduct?.manufacturingDate ? new Date(matchedProduct.manufacturingDate) : null
                                                                    );
                                                                    setValue(
                                                                        `vendorDetails.${index}.expiryDate`,
                                                                        matchedProduct?.expiryDate ? new Date(matchedProduct.expiryDate) : null
                                                                    );
                                                                } else {
                                                                    setValue(`vendorDetails.${index}.hsncode`, "");
                                                                    setValue(`vendorDetails.${index}.unitPrice`, "");
                                                                    setValue(`vendorDetails.${index}.quantityType`, "");
                                                                    setValue(`vendorDetails.${index}.highLevelCustomer`, "");
                                                                    setValue(`vendorDetails.${index}.midLevelCustomer`, "");
                                                                    setValue(`vendorDetails.${index}.lowLevelCustomer`, "");
                                                                    setValue(`vendorDetails.${index}.stock`, "In Stock");
                                                                    setValue(`vendorDetails.${index}.manufacturingDate`, null);
                                                                    setValue(`vendorDetails.${index}.expiryDate`, null);
                                                                }
                                                            }}
                                                        />
                                                        {error && (
                                                            <p className="text-danger small mt-1">
                                                                {error?.message}
                                                            </p>
                                                        )}
                                                    </div>
                                                )}
                                            />
                                        </div>
                                        <div className="col-12 col-md-2 d-flex flex-column  gap-2 mb-0 p-0">
                                            <label className="executive_label">HSN Code</label>
                                            <Controller
                                                name={`vendorDetails.${index}.hsncode`}
                                                control={control}
                                                defaultValue=""
                                                render={({ field }) => (
                                                    <input
                                                        {...field}
                                                        type="text"
                                                        placeholder="HSN"
                                                        className="product_input_field"
                                                        readOnly
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div className="col-12 col-md-2 d-flex flex-column gap-2 mb-0 p-0">
                                            <label className="executive_label">
                                                Purchase Price<span style={{ color: "red" }}>*</span>
                                            </label>
                                            <Controller
                                                name={`vendorDetails.${index}.unitPrice`}
                                                control={control}
                                                rules={{ required: "Price is required" }}
                                                render={({ field, fieldState: { error } }) => (
                                                    <>
                                                        <input
                                                            {...field}
                                                            type="number"
                                                            step="any"
                                                            placeholder="Unit Price"
                                                            className="product_input_field"
                                                        />
                                                        {error && (
                                                            <p className="text-danger small">
                                                                {error?.message}
                                                            </p>
                                                        )}
                                                    </>
                                                )}
                                            />
                                        </div>
                                        <div className="col-12 col-md-2 d-flex flex-column gap-2 mb-0 p-0">
                                            <label className="executive_label">
                                                Quantity
                                                <span style={{ color: "red" }}>*</span>
                                            </label>
                                            <Controller
                                                name={`vendorDetails.${index}.quantity`}
                                                control={control}
                                                defaultValue=""
                                                rules={{ required: "Quantity is required" }}
                                                render={({ field, fieldState: error }) => (
                                                    <>
                                                        <input
                                                            {...field}
                                                            type="number"
                                                            step="any"
                                                            placeholder="Quantity"
                                                            className="product_input_field"
                                                        />
                                                        {error && (
                                                            <p className="text-danger small">
                                                                {error?.message}
                                                            </p>
                                                        )}
                                                    </>
                                                )}
                                            />
                                        </div>
                                        <div className="col-12 col-md-2 d-flex flex-column gap-2 mb-0 p-0">
                                            <label className="executive_label">Quantity Type<span style={{ color: "red" }}>*</span></label>
                                            <Controller
                                                name={`vendorDetails.${index}.quantityType`}
                                                control={control}
                                                defaultValue=""
                                                rules={{ required: "Quantity Type is required" }}
                                                render={({ field, fieldState: { error } }) => {
                                                    const qtyOptions = (Array.isArray(quantityTypesData) ? quantityTypesData : []).map(qt => ({
                                                        id: qt.id || qt.quantityTypeId,
                                                        label: qt.name || qt.quantityTypeName,
                                                        value: qt.name || qt.quantityTypeName,
                                                        originalData: qt
                                                    }));

                                                    const selectedOption = qtyOptions.find(opt => opt.value === field.value) || null;

                                                    return (
                                                        <>
                                                            <Select
                                                                {...field}
                                                                isLoading={quantityTypesLoading}
                                                                options={qtyOptions}
                                                                value={selectedOption}
                                                                onChange={(selected) => field.onChange(selected ? selected.value : '')}
                                                                components={{ Option: CustomOption }}
                                                                onEditItem={handleEditQuantityType}
                                                                onDeleteItem={handleDeleteQuantityType}
                                                                placeholder="Select Qty"
                                                                classNamePrefix="react-select"
                                                                menuPortalTarget={document.body}
                                                                styles={{
                                                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                                                    control: (base, state) => ({
                                                                        ...base,
                                                                        minHeight: '38px',
                                                                        height: '38px',
                                                                        borderRadius: '0.375rem',
                                                                        borderColor: state.isFocused ? '#86b7fe' : '#ced4da',
                                                                        boxShadow: state.isFocused ? '0 0 0 .25rem rgba(13,110,253,.25)' : 'none',
                                                                    }),
                                                                    valueContainer: (base) => ({
                                                                        ...base,
                                                                        padding: '0 8px',
                                                                        height: '38px',
                                                                    }),
                                                                    input: (base) => ({
                                                                        ...base,
                                                                        margin: 0,
                                                                        padding: 0,
                                                                    }),
                                                                    indicatorsContainer: (base) => ({
                                                                        ...base,
                                                                        height: '38px',
                                                                    }),
                                                                }}
                                                            />
                                                            {error && (
                                                                <p className="text-danger small mt-1 mb-0">
                                                                    {error?.message}
                                                                </p>
                                                            )}
                                                        </>
                                                    );
                                                }}
                                            />
                                        </div>

                                        {watch(`vendorDetails.${index}.quantityType`) === "Other" && (
                                            <div className="col-12 col-md-2 d-flex flex-column gap-2 mb-0 p-0">
                                                <label className="executive_label">Other Qty Type</label>
                                                <Controller
                                                    name={`vendorDetails.${index}.otherQuantityType`}
                                                    control={control}
                                                    defaultValue=""
                                                    render={({ field }) => (
                                                        <input
                                                            {...field}
                                                            type="text"
                                                            placeholder="Enter Qty Type"
                                                            className="product_input_field"
                                                        />
                                                    )}
                                                />
                                            </div>
                                        )}
                                        <div className="col-12 col-md-2 d-flex flex-column  gap-2 mb-3 p-0">
                                            <label className="executive_label">GST (%)</label>
                                            <Controller
                                                name={`vendorDetails.${index}.gstPercentage`}
                                                control={control}
                                                defaultValue=""
                                                render={({ field }) => (
                                                    <input
                                                        {...field}
                                                        type="number"
                                                        step="any"
                                                        placeholder="GST"
                                                        className="product_input_field"
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div className="col-12 col-md-2 d-flex flex-column  gap-2 mb-3 p-0">
                                            <label className="executive_label">
                                                Manufacturing Date
                                            </label>
                                            <Controller
                                                name={`vendorDetails.${index}.manufacturingDate`}
                                                control={control}
                                                defaultValue={null}
                                                render={({ field }) => (
                                                    <DatePicker
                                                        {...field}
                                                        selected={field.value}
                                                        onChange={(date) => field.onChange(date)}
                                                        maxDate={new Date()}
                                                        placeholderText="YYYY-MM-DD"
                                                        dateFormat="yyyy-MM-dd"
                                                        className="product_input_field"
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div className="col-12 col-md-2 d-flex flex-column gap-2 mb-3 p-0">
                                            <label className="executive_label">Expiry Date</label>
                                            <Controller
                                                name={`vendorDetails.${index}.expiryDate`}
                                                control={control}
                                                defaultValue={null}
                                                render={({ field }) => (
                                                    <DatePicker
                                                        {...field}
                                                        selected={field.value}
                                                        onChange={(date) => field.onChange(date)}
                                                        minDate={watch(`vendorDetails.${index}.manufacturingDate`)}
                                                        placeholderText="YYYY-MM-DD"
                                                        dateFormat="yyyy-MM-dd"
                                                        className="product_input_field"
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div className="col-12 col-md-2 d-flex flex-column gap-2 mb-3 p-0">
                                            <label className="executive_label">Stock</label>
                                            <Controller
                                                name={`vendorDetails.${index}.stock`}
                                                control={control}
                                                defaultValue="In Stock"
                                                render={({ field }) => (
                                                    <select {...field} className="product_input_field">
                                                        <option value=""> Stock </option>
                                                        <option value="In Stock">In Stock</option>
                                                        <option value="Out Of Stock">Out Of Stock</option>
                                                    </select>
                                                )}
                                            />
                                        </div>
                                        <div className="col-12 col-md-2 d-flex flex-column gap-2 mb-1 p-0">
                                            <label className="executive_label">MRP</label>
                                            <Controller
                                                name={`vendorDetails.${index}.highLevelCustomer`}
                                                control={control}
                                                defaultValue=""
                                                render={({ field }) => (
                                                    <input
                                                        {...field}
                                                        type="text"
                                                        placeholder="00.00"
                                                        className="product_input_field"
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div className="col-12 col-md-2 d-flex flex-column gap-2 mb-1 p-0">
                                            <label className="executive_label">Retailer</label>
                                            <Controller
                                                name={`vendorDetails.${index}.midLevelCustomer`}
                                                control={control}
                                                defaultValue=""
                                                render={({ field }) => (
                                                    <input
                                                        {...field}
                                                        type="text"
                                                        placeholder="00.00"
                                                        className="product_input_field"
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div className="col-12 col-md-2 d-flex flex-column gap-2 mb-1 p-0">
                                            <label className="executive_label">Wholesale</label>
                                            <Controller
                                                name={`vendorDetails.${index}.lowLevelCustomer`}
                                                control={control}
                                                defaultValue=""
                                                render={({ field }) => (
                                                    <input
                                                        {...field}
                                                        type="text"
                                                        placeholder="00.00"
                                                        className="product_input_field"
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div className="col-12 col-md-2 d-flex flex-column gap-2 mb-1 p-0">
                                            <label className="executive_label">Other Amount</label>
                                            <Controller
                                                name={`vendorDetails.${index}.otherAmount`}
                                                control={control}
                                                defaultValue="0.00"
                                                render={({ field }) => (
                                                    <input
                                                        {...field}
                                                        type="number"
                                                        step="any"
                                                        placeholder="00.00"
                                                        className="product_input_field"
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div className="col-12 col-md-2 d-flex flex-column gap-2 mb-1 p-0">
                                            <label className="executive_label">GST Amount</label>
                                            <Controller
                                                name={`vendorDetails.${index}.gstAmount`}
                                                control={control}
                                                defaultValue="0.00"
                                                render={({ field }) => (
                                                    <input
                                                        {...field}
                                                        type="text"
                                                        className="product_input_field"
                                                        readOnly
                                                        disabled
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div className="col-12 col-md-2 d-flex flex-column gap-2 mb-1 p-0">
                                            <label className="executive_label">Total</label>
                                            <Controller
                                                name={`vendorDetails.${index}.totalAmount`}
                                                control={control}
                                                defaultValue="0.00"
                                                render={({ field }) => (
                                                    <input
                                                        {...field}
                                                        type="text"
                                                        className="product_input_field"
                                                        style={{ fontWeight: "bold", backgroundColor: "#f8f9fa" }}
                                                        readOnly
                                                        disabled
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div
                                            className="col-12 col-md-2 d-flex justify-content-start align-items-end gap-2 mb-1 p-0"
                                            style={{ width: "8%" }}
                                            onClick={() => {
                                                append({
                                                    product: "",
                                                    quantity: "",
                                                    unitPrice: "",
                                                    discountPrice: "",
                                                    actualPrice: "",
                                                    hsnCode: "",
                                                    gstPercentage: "",
                                                    manufacturingDate: "",
                                                    expiryDate: "",
                                                    lowLevelCustomer: "",
                                                    midLevelCustomer: "",
                                                    highLevelCustomer: "",
                                                    others: "",
                                                    stock: "In Stock",
                                                    otherAmount: "0.00",
                                                    gstAmount: "0.00",
                                                    totalAmount: "0.00",
                                                });
                                            }}
                                        >
                                            <div
                                                style={{
                                                    width: "40px",
                                                    height: "40px",
                                                    cursor: "pointer",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    backgroundColor: "#007bff",
                                                    color: "white",
                                                    borderRadius: "5px",
                                                    fontSize: "24px"
                                                }}
                                            >
                                                <span>+</span>
                                            </div>
                                        </div>
                                        {index > 0 && (
                                            <div className="col-12 col-md-1 d-flex justify-content-start align-items-end mb-1 p-0">
                                                <button
                                                    type="button"
                                                    onClick={() => remove(index)}
                                                    style={{
                                                        border: "none",
                                                        borderRadius: "5px",
                                                        height: "35px",
                                                        fontWeight: "bolder",
                                                        background: "grey",
                                                        color: "white",
                                                        padding: "0 10px"
                                                    }}
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    {/* <div className="row px-3 d-flex gap-3 my-2 append_order">

                                    </div> */}
                                </div>
                            );
                        })}

                        <div className="row px-3 d-flex gap-3 my-4">
                            <div className="col-12 col-md-2 d-flex flex-column gap-2 mb-3 p-0">
                                <label className="executive_label">Register ID</label>
                                <Controller
                                    name="registerId"
                                    control={control}
                                    defaultValue=""
                                    render={({ field }) => (
                                        <input
                                            {...field}
                                            value={watch('registerId') || field.value || ''}
                                            type="text"
                                            className="product_input_field"
                                            disabled
                                        />
                                    )}
                                />
                            </div>

                            <div className="col-12 col-md-2 d-flex flex-column gap-2 mb-3 p-0">
                                <label className="executive_label">Purchase Date</label>
                                <Controller
                                    name="purchaseDate"
                                    control={control}
                                    defaultValue=""
                                    render={({ field }) => (
                                        <input
                                            {...field}
                                            type="date"
                                            className="product_input_field"
                                        />
                                    )}
                                />
                            </div>

                            <div className="col-12 col-md-2 d-flex flex-column gap-2 mb-3 p-0">
                                <label className="executive_label">Invoice No</label>
                                <Controller
                                    name="invoiceNumber"
                                    control={control}
                                    defaultValue=""
                                    render={({ field }) => (
                                        <input
                                            {...field}
                                            type="text"
                                            placeholder="Invoice No"
                                            className="product_input_field"
                                        />
                                    )}
                                />
                            </div>
                            <div className="col-12 col-md-2 d-flex flex-column gap-2 mb-3 p-0">
                                <label className="executive_label">Transaction Mode</label>
                                <Controller
                                    name="transactionMode"
                                    control={control}
                                    defaultValue="Online"
                                    render={({ field }) => (
                                        <select {...field} className="product_input_field">
                                            <option value="">None</option>
                                            <option value="Cash">Cash</option>
                                            <option value="Online">Online</option>
                                        </select>
                                    )}
                                />
                            </div>
                            <div className="col-12 col-md-2 d-flex flex-column gap-2 mb-3 p-0">
                                <label className="executive_label">Status</label>
                                <Controller
                                    name="transactionStatus"
                                    control={control}
                                    defaultValue="Pending"
                                    render={({ field }) => (
                                        <select {...field} className="product_input_field">
                                            <option value="">None</option>
                                            <option value="Pending">Pending</option>
                                            <option value="Completed">Completed</option>
                                        </select>
                                    )}
                                />
                            </div>
                        </div>

                        <div className="row px-3 my-4">
                            <div className="col-12 col-md-2 p-0">
                                <button className="product_form_submit_btn">
                                    {postLoading || putLoading ? "Submitting..." : "Submit"}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div >
            <CreateQuantityType editData={editingQuantityType} onClose={() => setEditingQuantityType(null)} />
        </>
    );
};

export default NewPurchase;
